import React, {FC, useEffect, useState} from 'react'
import {CustomModal} from '_metronic/partials/modals/CustomModal'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import Input from 'app/components/Input'
import Select from '../Select'
import {MenuListProps, OptionProps, SingleValue, SingleValueProps} from 'react-select'
import {toAbsoluteUrl} from '_metronic/helpers'
import {useAuth} from 'app/modules/auth'
import {deleteObject, getDownloadURL, ref, uploadBytes} from 'firebase/storage'
import db, {storage} from 'services/firebase'
import Dropzone from 'react-dropzone'
import {PolicyDataType} from '../Policies'
import {FilterOptionOption} from 'react-select/dist/declarations/src/filters'
import {addDoc, collection, doc, serverTimestamp, setDoc, updateDoc} from '@firebase/firestore'
import Button from 'app/components/Button'
import {showSuccessToast} from 'app/components/ShowToast'

type Props = {
  isModalOpen: boolean
  hideModal: () => void
  isEditMode: boolean
  policy?: PolicyDataType
  availablePolicies: {id?: string; title: string; documentURL?: string}[]
}

type CustomOptionType = {
  id?: string
  title: string
  documentURL?: string
}

const accessArr = [
  {title: 'Restricted', iconPath: '/media/icon/lock.svg'},
  {title: 'Public', iconPath: '/media/icon/world.svg'},
]

const CustomOptionComponent = (props: OptionProps<CustomOptionType>) => {
  const {data, innerRef, innerProps} = props

  return (
    <div
      ref={innerRef}
      className='d-flex align-items-center cursor-pointer'
      style={{
        gap: '8px',
      }}
      {...innerProps}
    >
      <img
        alt={`${data.title.toLowerCase() === 'upload document' ? 'upload-icon' : 'document-icon'}`}
        src={`${
          data.title.toLowerCase() === 'upload document'
            ? toAbsoluteUrl('/media/icon/upload.svg')
            : toAbsoluteUrl('/media/icon/file-text.svg')
        }`}
        className=''
        style={{height: '16px', width: '16px'}}
      />
      <span className='text-dark fs-6'>{data.title}</span>
    </div>
  )
}
const menuListComponent = (props: MenuListProps<CustomOptionType>) => {
  const {children} = props

  const standardChildren: React.ReactNode[] = []
  let customChild: React.ReactNode | null = null

  React.Children.forEach(children, (child: any) => {
    if (child?.props?.data?.title.toLowerCase() === 'upload document') {
      customChild = child
    } else {
      standardChildren.push(child)
    }
  })

  return (
    <div
      style={{
        padding: '16px',
      }}
    >
      <div
        style={{
          maxHeight: '150px',
          overflowY: 'auto',
          display: 'flex',
          gap: '12px',
          flexDirection: 'column',
        }}
      >
        {standardChildren}
      </div>

      {customChild &&
        (standardChildren.length > 0 ? (
          <div
            style={{
              marginTop: '12px',
              borderTop: '1px solid #E5E7EB',
              paddingTop: '12px',
            }}
          >
            {customChild}
          </div>
        ) : (
          <div>{customChild}</div>
        ))}
    </div>
  )
}

const SingleValueComponent = (props: SingleValueProps<CustomOptionType>) => {
  const {data, innerProps} = props
  return (
    <div
      className='d-flex align-items-center '
      style={{gap: '8px', position: 'absolute', left: '13px'}}
      {...innerProps}
    >
      <img
        alt={`${data.title.toLowerCase() === 'upload document' ? 'upload-icon' : 'document-icon'}`}
        src={`${
          data.title.toLowerCase() === 'upload document'
            ? toAbsoluteUrl('/media/icon/upload.svg')
            : toAbsoluteUrl('/media/icon/file-text.svg')
        }`}
        className=''
        style={{height: '16px', width: '16px'}}
      />
      <span className='fw-medium fs-6 text-truncate' style={{color: '#4b5675'}}>
        {data.title}
      </span>
    </div>
  )
}

const filterOption = (option: FilterOptionOption<CustomOptionType>, inputValue: string) => {
  return option.data?.title.toLowerCase().includes(inputValue.toLowerCase())
}

const SUPPORTED_FILE_FORMATS = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]

const initialValues = {
  id: '',
  title: '',
  documentName: '',
  documentURL: '',
  access: 'Restricted',
}

const validationSchema = Yup.object({
  title: Yup.string()
    .min(3, 'Minimum 3 characters')
    .max(200, 'Maximum 200 characters')
    .required('Name is required'),
  documentURL: Yup.mixed<File | string>()
    .required('Document is required')
    .test('fileFormat', 'Unsupported format for document', (value) => {
      if (typeof value === 'string') return true

      if (value instanceof File) {
        return value && SUPPORTED_FILE_FORMATS.includes(value.type)
      }
      return false
    })
    .test('fileSize', 'Document size is too large', (value) => {
      if (typeof value === 'string') return true

      if (value instanceof File) {
        return value && value.size <= 5 * 1024 * 1024
      }
      return false
    }),
  access: Yup.string()
    .oneOf(['Restricted', 'Public'], 'Access must be either restricted or public')
    .required('Access is required'),
})

const AddEditPolicyModal: FC<Props> = ({
  isModalOpen,
  hideModal,
  isEditMode,
  policy,
  availablePolicies,
}) => {
  const {currentUser} = useAuth()

  const [policyId, setPolicyId] = useState('')

  const [loading, setLoading] = useState(false)

  const [isUploadDocumentOptionSelected, setIsUploadDocumentOptionSelected] = useState(false)

  const [uploadedFile, setUploadedFile] = useState<File>()

  const [selectedOption, setSelectedOption] = useState<SingleValue<CustomOptionType>>()

  const [isDropzoneShownForEditMode, setIsDropzoneShownForEditMode] = useState(false)

  const [selectError, setSelectError] = useState('')

  const currentDate = new Date()
  const year = currentDate.getFullYear()
  const month = currentDate.getMonth() + 1
  const day = currentDate.getDate()
  const todaysDate = month + '/' + day + '/' + year

  const formik = useFormik<PolicyDataType>({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true)
      try {
        const tenantId = currentUser?.tenantId || ''
        const policiesRef = collection(
          db,
          'tenants',
          tenantId,
          'trust_center',
          'policies',
          'all_policies'
        )

        if (!isEditMode) {
          let downloadURL = ''
          let fileName = ''
          let fileId = ''

          if (values.documentURL instanceof File) {
            fileName = values.documentURL.name
            fileId = `${Date.now()}`

            const fileRef = ref(
              storage,
              `tenants/${currentUser?.tenantId}/trust_center/policies/${fileId}-${fileName}`
            )
            const uploadResult = await uploadBytes(fileRef, values.documentURL)
            downloadURL = await getDownloadURL(uploadResult.ref)
          }

          const newDocRef = await addDoc(policiesRef, {
            title: values.title,
            documentName: downloadURL.length > 0 ? fileName : values.documentName,
            documentURL: downloadURL.length > 0 ? downloadURL : values.documentURL,
            documentId: downloadURL.length > 0 ? fileId : '',
            access: values.access,
            policyId: policyId,
            isCustomDocumentUploaded: downloadURL.length > 0 ? true : false,
          })

          await updateDoc(newDocRef, {
            id: newDocRef.id,
          })

          const activitiesRef = collection(db, 'tenants', tenantId, 'activities')

          await addDoc(activitiesRef, {
            message: `${values.title} policy for trust center has been added`,
            timestamp: serverTimestamp(),
            tenantId: currentUser?.tenantId,
            uid: currentUser?.uid,
            username: currentUser?.userName,
            date: todaysDate,
            email: currentUser?.email,
            target: 'Trust Center',
            action: 'Policy added',
          })

          showSuccessToast({message: 'New policy has been added successfully'})
        } else {
          const docRef = doc(policiesRef, values.id)
          let isOldFileDeleted = false

          if (policy?.isCustomDocumentUploaded && policy?.documentURL !== values.documentURL) {
            const fileRef = ref(
              storage,
              `tenants/${currentUser?.tenantId}/trust_center/policies/${policy?.documentId}-${policy?.documentName}`
            )

            await deleteObject(fileRef)

            isOldFileDeleted = true
          }

          let downloadURL = ''
          let fileName = ''
          let fileId = ''

          if (values.documentURL instanceof File) {
            fileName = values.documentURL.name
            fileId = `${Date.now()}`

            const fileRef = ref(
              storage,
              `tenants/${currentUser?.tenantId}/trust_center/policies/${fileId}-${fileName}`
            )
            const uploadResult = await uploadBytes(fileRef, values.documentURL!)
            downloadURL = await getDownloadURL(uploadResult.ref)
          }

          await setDoc(docRef, {
            id: docRef.id,
            title: values.title,
            documentName: downloadURL.length > 0 ? fileName : values.documentName,
            documentURL: downloadURL.length > 0 ? downloadURL : values.documentURL,
            documentId: downloadURL.length > 0 ? fileId : isOldFileDeleted ? '' : values.documentId,
            access: values.access,
            policyId: downloadURL.length > 0 ? '' : values.policyId,
            isCustomDocumentUploaded:
              downloadURL.length > 0
                ? true
                : isOldFileDeleted
                ? false
                : values?.isCustomDocumentUploaded,
          })

          const activitiesRef = collection(db, 'tenants', tenantId, 'activities')

          await addDoc(activitiesRef, {
            message: `${policy?.title} policy for trust center has been updated`,
            timestamp: serverTimestamp(),
            tenantId: currentUser?.tenantId,
            uid: currentUser?.uid,
            username: currentUser?.userName,
            date: todaysDate,
            email: currentUser?.email,
            target: 'Trust Center',
            action: 'Policy updated',
          })

          showSuccessToast({message: 'Policy has been updated successfully'})
        }
      } catch (error) {
        console.log('Error while updating policy', error)
      } finally {
        setLoading(false)
        hideModal()
      }
    },
  })

  useEffect(() => {
    if (policy?.title) {
      formik.setValues({
        id: policy!.id,
        title: policy!.title,
        documentName: policy.documentName,
        documentURL: policy!.documentURL as string,
        documentId: policy!.documentId,
        access: policy!.access,
        policyId: policy!.policyId,
        isCustomDocumentUploaded: policy.isCustomDocumentUploaded ?? false,
      })
    }

    setSelectedOption(() => {
      return isEditMode
        ? policy?.isCustomDocumentUploaded ?? false
          ? {
              title: 'Upload document',
            }
          : {
              id: policy?.id,
              title: policy!.documentName,
              documentURL: policy!.documentURL as string,
            }
        : null
    })

    if (policy?.isCustomDocumentUploaded) {
      setIsUploadDocumentOptionSelected(true)
    }
  }, [policy])

  const okBtnClickHandler = () => {
    if (!selectedOption) {
      setSelectError('Document is required')
    }
    formik.submitForm()
  }

  const handlePolicyChange = (option: SingleValue<CustomOptionType>) => {
    setSelectedOption(option)
    setSelectError('')
    if (option?.title.toLowerCase() === 'upload document') {
      setIsUploadDocumentOptionSelected(true)
      formik.setFieldValue('title', '')
      formik.setFieldValue('documentName', '')
      formik.setFieldValue('documentURL', null)
      setUploadedFile(undefined)
      setPolicyId('')
    } else {
      setIsUploadDocumentOptionSelected(false)
      formik.setFieldValue('title', option?.title)
      formik.setFieldValue('documentName', option?.title)
      formik.setFieldValue('documentURL', option?.documentURL)
      setPolicyId(option?.id || '')
    }
  }

  const handleRemoveFile = () => {
    setUploadedFile(undefined)
    formik.setFieldValue('documentURL', null)
    formik.setFieldValue('documentName', '')
    setIsDropzoneShownForEditMode(true)
  }

  const handleUpload = (acceptedFile: File[]) => {
    setUploadedFile(acceptedFile[0])
    formik.setFieldTouched('documentURL', true)
    formik.setFieldValue('documentURL', acceptedFile[0])
  }

  return (
    <CustomModal
      isOpen={isModalOpen}
      onClose={hideModal}
      title={`${isEditMode ? 'Edit Policy' : 'Add Policy'}`}
      okBtnText={`${isEditMode ? 'Save' : 'Add Policy'}`}
      okBtnClickHandler={okBtnClickHandler}
      isCloseIconShown={true}
      isLoading={loading}
      isLoadingBtnText={`${isEditMode ? 'Saving...' : 'Adding Policy...'}`}
    >
      <div
        className='d-flex flex-column align-items-center justify-content-center'
        style={{gap: '20px'}}
      >
        <Select<CustomOptionType>
          label='Document'
          options={[
            ...availablePolicies,
            {
              title: 'Upload Document',
            },
          ]}
          placeholder='Select Policy'
          optionComponent={CustomOptionComponent}
          singleValueComponent={SingleValueComponent}
          menuListComponent={menuListComponent}
          filterOption={filterOption}
          onChange={(value) => handlePolicyChange(value as SingleValue<CustomOptionType>)}
          value={selectedOption}
          isClearable={false}
          error={selectError}
          isDisabled={isEditMode}
        />

        <Input
          type='text'
          label='Policy Name'
          placeholder='Policy Name'
          error={formik.touched.title && formik.errors.title ? formik.errors.title : ''}
          {...formik.getFieldProps('title')}
        />

        {isUploadDocumentOptionSelected && (
          <div className='w-100 d-flex flex-column' style={{gap: '8px'}}>
            {(isEditMode && policy?.isCustomDocumentUploaded && !isDropzoneShownForEditMode) ||
            uploadedFile ? (
              <div
                className=''
                style={{border: '1px solid #E5E7EB', borderRadius: '8px', padding: '16px'}}
              >
                <div className='d-flex align-items-center' style={{gap: '16px'}}>
                  <div
                    className='d-flex align-items-center justify-content-center'
                    style={{
                      backgroundColor: '#F3F4F6',
                      borderRadius: '12px',
                      height: '48px',
                      width: '48px',
                    }}
                  >
                    <img
                      alt='file-text-icon'
                      src={toAbsoluteUrl('/media/icon/file-text.svg')}
                      className=''
                      style={{height: '32px', width: '32px'}}
                    />
                  </div>

                  <span className='flex-fill text-dark fs-6 fw-medium'>
                    {uploadedFile?.name ?? policy?.documentName}
                  </span>

                  <Button
                    style={{
                      width: '34px',
                      height: '34px',
                    }}
                    onClick={handleRemoveFile}
                  >
                    <img
                      alt='trash-icon'
                      src={toAbsoluteUrl('/media/icon/trash.svg')}
                      className=''
                      style={{height: '18px', width: '18px'}}
                    />
                  </Button>
                </div>
              </div>
            ) : (
              <Dropzone
                onDrop={handleUpload}
                minSize={1024}
                accept={{
                  'application/pdf': ['.pdf'],
                  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
                    '.docx',
                  ],
                }}
                maxSize={5 * 1024 * 1024}
              >
                {({getRootProps, getInputProps}) => (
                  <div
                    {...getRootProps({
                      className: 'dropzone',
                      style: {
                        backgroundColor: 'white',
                        border: '2px dashed #E5E7EB',
                        borderRadius: '8px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '24px 32px',
                        textAlign: 'center',
                        cursor: 'pointer',
                      },
                    })}
                  >
                    <input {...getInputProps()} />

                    <div className='d-flex flex-column align-items-center' style={{gap: '16px'}}>
                      <div className='d-flex flex-column align-items-center' style={{gap: '7px'}}>
                        <div
                          className='d-flex align-items-center justify-content-center'
                          style={{
                            backgroundColor: '#F3F4F6',
                            borderRadius: '12px',
                            height: '48px',
                            width: '48px',
                          }}
                        >
                          <img
                            alt='document-icon'
                            src={toAbsoluteUrl('/media/icon/file-text.svg')}
                            className=''
                            style={{height: '32px', width: '32px'}}
                          />
                        </div>

                        <span className='text-dark fs-6 fw-medium'>Drag and drop file here</span>

                        <span className='text-secondary-gray' style={{fontSize: '13px'}}>
                          Supported files: pdf, doc, docx. Max 5MB.
                        </span>
                      </div>

                      <div>
                        <Button>
                          <img
                            alt='upload-icon'
                            src={toAbsoluteUrl('/media/icon/upload.svg')}
                            className=''
                            style={{height: '18px', width: '18px'}}
                          />
                          <span className='text-dark fs-6 fw-medium'>Upload File</span>
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </Dropzone>
            )}

            {formik.touched.documentURL && formik.errors.documentURL && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.documentURL}</span>
                </div>
              </div>
            )}
          </div>
        )}

        <div className='w-100 d-flex flex-column justify-content-center' style={{gap: '8px'}}>
          <label className='fw-medium text-dark fs-6'>Access</label>

          <div role='radio' className='d-flex align-items-center' style={{gap: '12px'}}>
            {accessArr.map((access, idx) => (
              <div
                key={idx}
                className='flex-fill cursor-pointer'
                style={{
                  backgroundColor: `${formik.values.access === access.title ? '#F9FAFB' : 'white'}`,
                  border: `${
                    formik.values.access === access.title ? '2px solid black' : '2px solid #E5E5E5'
                  }`,
                  borderRadius: '8px',
                  padding: '8px 16px',
                }}
                onClick={() => {
                  formik.setFieldValue('access', access.title)
                }}
              >
                <div
                  className='d-flex justify-content-center align-items-center'
                  style={{gap: '6px'}}
                >
                  <img
                    alt={'icon'}
                    src={toAbsoluteUrl(access.iconPath)}
                    className=''
                    style={{height: '16px', width: '16px'}}
                  />
                  <span className='text-dark fs-6'>{access.title}</span>
                </div>
              </div>
            ))}
          </div>

          {formik.touched.access && formik.errors.access && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.access}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </CustomModal>
  )
}

export default AddEditPolicyModal
